import { LocalVideoTrack } from 'livekit-client'
import BackgroundTransformer, { BackgroundOptions } from './transformers/BackgroundTransformer'

/** Частота захвата обработанного канваса. Выше нет смысла: камера шлёт не больше 30. */
const CAPTURE_FPS = 30

const MIN_FRAME_INTERVAL_MS = 1000 / CAPTURE_FPS

/**
 * Обходной путь обработки фона для браузеров без потоковых API трека (Firefox).
 * Рисует обработанные кадры на скрытом DOM-канвасе через общую функцию компоновки
 * трансформера, забирает видеотрек через canvas.captureStream() и подменяет им
 * публикуемый трек камеры. При остановке возвращает оригинальный трек обратно.
 * Дешёвый блюр из трансформера здесь обязателен: канвас-блюр Firefox считается на CPU.
 */
export class CanvasCaptureBackgroundPublisher {
  private video?: HTMLVideoElement

  private canvas?: HTMLCanvasElement

  private captureCtx?: CanvasRenderingContext2D | null

  private outputCanvas?: OffscreenCanvas

  private capturedStream?: MediaStream

  private originalTrack?: MediaStreamTrack

  private transformer?: BackgroundTransformer

  private rafId = 0

  private running = false

  private attached = false

  private lastFrameTime = 0

  constructor(
    private localTrack: LocalVideoTrack,
    private options: BackgroundOptions,
  ) {}

  async start() {
    await this.startPreview()
    try {
      await this.attachToPublishedTrack()
    } catch (error) {
      await this.stop()
      throw error
    }
  }

  /**
   * Запускает конвейер и захват без подмены трека: режим превью до входа.
   * Возвращает стрим обработанных кадров для показа в <video>.
   */
  async startPreview(): Promise<MediaStream> {
    if (this.capturedStream) {
      return this.capturedStream
    }
    this.originalTrack = this.localTrack.mediaStreamTrack
    const settings = this.originalTrack.getSettings()
    const width = settings.width ?? 1280
    const height = settings.height ?? 720
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.srcObject = new MediaStream([this.originalTrack])
    await this.video.play()
    this.outputCanvas = new OffscreenCanvas(width, height)
    this.transformer = new BackgroundTransformer(this.options)
    await this.transformer.init({ outputCanvas: this.outputCanvas, inputElement: this.video })
    this.canvas = document.createElement('canvas')
    this.canvas.width = width
    this.canvas.height = height
    this.captureCtx = this.canvas.getContext('2d')
    this.capturedStream = this.canvas.captureStream(CAPTURE_FPS)
    this.running = true
    this.lastFrameTime = 0
    this.loop()
    return this.capturedStream
  }

  /**
   * Подменяет публикуемый трек захваченным. На неопубликованном треке
   * livekit бросает исключение — тогда издатель остаётся в режиме превью.
   */
  async attachToPublishedTrack(): Promise<void> {
    if (this.attached || !this.capturedStream) {
      return
    }
    const [captureTrack] = this.capturedStream.getVideoTracks()
    await this.localTrack.replaceTrack(captureTrack)
    this.attached = true
  }

  getPreviewStream(): MediaStream | undefined {
    return this.capturedStream
  }

  updateOptions(options: BackgroundOptions) {
    this.options = options
    void this.transformer?.update(options)
  }

  async stop() {
    this.running = false
    this.attached = false
    cancelAnimationFrame(this.rafId)
    this.capturedStream?.getTracks().forEach((track) => track.stop())
    this.capturedStream = undefined
    if (this.originalTrack) {
      try {
        await this.localTrack.replaceTrack(this.originalTrack)
      } catch {
        // Трек уже снят с публикации или комната закрыта — возвращать нечего.
      }
      this.originalTrack = undefined
    }
    if (this.video) {
      this.video.pause()
      this.video.srcObject = null
      this.video = undefined
    }
    await this.transformer?.destroy()
    this.transformer = undefined
  }

  private loop = () => {
    if (!this.running) {
      return
    }
    this.rafId = requestAnimationFrame(this.loop)
    const now = performance.now()
    if (now - this.lastFrameTime < MIN_FRAME_INTERVAL_MS) {
      return
    }
    this.lastFrameTime = now
    void this.renderOnce()
  }

  private async renderOnce() {
    if (!this.transformer || !this.video || !this.captureCtx || !this.canvas || !this.outputCanvas) {
      return
    }
    if (this.video.readyState < 2 || this.video.videoWidth === 0) {
      return
    }
    await this.transformer.processSource(this.video, this.video.videoWidth, this.video.videoHeight)
    this.captureCtx.drawImage(this.outputCanvas, 0, 0, this.canvas.width, this.canvas.height)
  }
}
